import type { ChunkHash, ModelIntegrity } from "./protocol";

/**
 * 모델 캐시(Cache API) 공용 포맷.
 *
 * 워커(추론)와 다운로드 매니저가 같은 키/매니페스트를 써야 한다.
 * - 청크 키: <base>?chunk=<index>
 * - 매니페스트 키: <base>?manifest=1
 * - base: cacheKey + "?v=<전체 sha256>" (콘텐츠 해시로 버전 결합)
 */
export const CACHE_NAME = "transformers-cache";
export const CHUNK_SIZE = 64 * 1024 * 1024;
// v2: 전체 SHA-256 + 청크 해시를 저장·검증한다. v1 캐시는 자동으로 miss 된다.
export const MANIFEST_VERSION = 2;

const HEX64 = /^[0-9a-f]{64}$/;

export type ModelManifest = {
  version: number;
  sha256: string;
  chunkSize: number;
  totalBytes: number;
  chunks: ChunkHash[];
};

export function isSha256Hex(value: unknown): value is string {
  return typeof value === "string" && HEX64.test(value);
}

export const withParam = (base: string, key: string, value: string | number) =>
  `${base}${base.includes("?") ? "&" : "?"}${key}=${value}`;
export const chunkKey = (base: string, index: number) => withParam(base, "chunk", index);
export const manifestKey = (base: string) => withParam(base, "manifest", "1");

export function cacheBase(cacheKey: string, sha256: string): string {
  return withParam(cacheKey, "v", sha256);
}

export function concat(parts: Uint8Array[], total: number): Uint8Array {
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.byteLength;
  }
  return out;
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // TS 7의 DOM 타입은 ArrayBuffer 전용 뷰를 요구한다. SharedArrayBuffer는 쓰지 않는다.
  const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource);
  let out = "";
  for (const byte of new Uint8Array(digest)) out += byte.toString(16).padStart(2, "0");
  return out;
}

export async function digestChunks(bytes: Uint8Array, chunkSize: number): Promise<ChunkHash[]> {
  const chunks: ChunkHash[] = [];
  for (let offset = 0, index = 0; offset < bytes.byteLength; offset += chunkSize, index++) {
    const part = bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength));
    chunks.push({ index, size: part.byteLength, sha256: await sha256Hex(part) });
  }
  return chunks;
}

/** 캐시 매니페스트만 읽는다(청크 본문 검증 없음). 준비 여부 빠른 확인용. */
export async function readCacheManifest(
  cache: Cache,
  base: string,
): Promise<ModelManifest | null> {
  try {
    const response = await cache.match(manifestKey(base));
    if (!response) return null;
    const manifest = (await response.json()) as ModelManifest;
    if (
      manifest.version !== MANIFEST_VERSION ||
      manifest.chunkSize !== CHUNK_SIZE ||
      !Number.isInteger(manifest.totalBytes) ||
      manifest.totalBytes <= 0 ||
      !isSha256Hex(manifest.sha256) ||
      !Array.isArray(manifest.chunks) ||
      manifest.chunks.length === 0
    ) {
      return null;
    }
    return manifest;
  } catch {
    return null;
  }
}

/** 매니페스트가 기대 해시/크기와 일치하는지 빠르게 확인한다. */
export async function hasCachedModel(
  cacheKey: string,
  integrity: ModelIntegrity | null,
): Promise<boolean> {
  if (!integrity) return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    const manifest = await readCacheManifest(cache, cacheBase(cacheKey, integrity.sha256));
    return Boolean(
      manifest &&
        manifest.sha256 === integrity.sha256 &&
        manifest.totalBytes === integrity.size &&
        manifest.chunks.length === integrity.chunks.length,
    );
  } catch {
    return false;
  }
}

export async function readCachedModel(
  cache: Cache,
  base: string,
  integrity: ModelIntegrity | null,
): Promise<{ bytes: Uint8Array; sha256: string } | null> {
  try {
    const manifest = await readCacheManifest(cache, base);
    if (!manifest) return null;

    if (integrity) {
      if (
        manifest.sha256 !== integrity.sha256 ||
        manifest.totalBytes !== integrity.size ||
        manifest.chunks.length !== integrity.chunks.length
      ) {
        return null;
      }
    }

    const bytes = new Uint8Array(manifest.totalBytes);
    let offset = 0;
    for (let index = 0; index < manifest.chunks.length; index++) {
      const info = manifest.chunks[index]!;
      const expected = integrity?.chunks[index];
      if (expected && (expected.size !== info.size || expected.sha256 !== info.sha256)) return null;

      const response = await cache.match(chunkKey(base, index));
      if (!response) return null;
      const part = new Uint8Array(await response.arrayBuffer());
      if (part.byteLength !== info.size) return null;
      if (offset + part.byteLength > bytes.byteLength) return null;
      if ((await sha256Hex(part)) !== info.sha256) {
        console.warn(`[beoptong] cached chunk ${index} failed verification; refetching`);
        return null;
      }
      bytes.set(part, offset);
      offset += part.byteLength;
    }

    if (offset !== bytes.byteLength) return null;
    return { bytes, sha256: manifest.sha256 };
  } catch (error) {
    console.warn("[beoptong] cached model read failed", error);
    return null;
  }
}

export async function putCachedChunk(
  cache: Cache,
  base: string,
  index: number,
  bytes: Uint8Array,
): Promise<void> {
  await cache.put(
    chunkKey(base, index),
    new Response(bytes as unknown as BodyInit),
  );
}

export async function writeCachedManifest(
  cache: Cache,
  base: string,
  sha256: string,
  totalBytes: number,
  chunks: ChunkHash[],
): Promise<void> {
  const manifest: ModelManifest = {
    version: MANIFEST_VERSION,
    sha256,
    chunkSize: CHUNK_SIZE,
    totalBytes,
    chunks,
  };
  await cache.put(
    manifestKey(base),
    new Response(JSON.stringify(manifest), { headers: { "content-type": "application/json" } }),
  );
}

export async function writeCachedModel(
  cache: Cache,
  base: string,
  bytes: Uint8Array,
  sha256: string,
  chunks: ChunkHash[],
): Promise<boolean> {
  try {
    for (const info of chunks) {
      const start = info.index * CHUNK_SIZE;
      await putCachedChunk(cache, base, info.index, bytes.subarray(start, start + info.size));
    }
    await writeCachedManifest(cache, base, sha256, bytes.byteLength, chunks);
    return true;
  } catch (error) {
    const dom = error as DOMException;
    console.warn(`[beoptong] cache put failed ${dom?.name}: ${dom?.message}`);
    return false;
  }
}
